import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useUploadEvidence } from '@/api/hooks/useCompliance';
import { cn } from '@/lib/utils';

// ── Shared evidence constants + helpers (imported by SubmitWithEvidenceModal) ──

export const EVIDENCE_ADVISORY_COPY =
  'Evidence details are recorded for audit traceability. Keep source files in your own systems and provide them directly to auditors on request.';

export const EVIDENCE_TYPES = [
  'policy_document', 'screenshot', 'log_export', 'assessment_report',
  'certificate', 'interview_notes', 'audit_report',
] as const;

export type EvidenceType = (typeof EVIDENCE_TYPES)[number];

export interface EvidenceDraft {
  title: string;
  evidenceType: EvidenceType | string;
  collectedAt: string;
  location: string;
  notes: string;
}

export function emptyDraft(): EvidenceDraft {
  return {
    title: '',
    evidenceType: 'screenshot',
    collectedAt: new Date().toISOString().substring(0, 10),
    location: '',
    notes: '',
  };
}

export function isDraftValid(d: EvidenceDraft): boolean {
  return d.title.trim().length >= 3 && Boolean(d.evidenceType) && Boolean(d.collectedAt);
}

export function buildContentSummary(location: string, notes: string): string | undefined {
  const parts = [
    location.trim() ? `Location: ${location.trim()}` : '',
    notes.trim(),
  ].filter(Boolean);
  return parts.length > 0 ? parts.join('\n\n---\n\n') : undefined;
}

// ── EvidenceCaptureModal ───────────────────────────────────────────────────────
// Pure capture: 5-field form, locked advisory copy, POST to /evidence endpoint.
// No state-machine awareness — use SubmitWithEvidenceModal for in_progress→submitted.

interface Props {
  open: boolean;
  onClose: () => void;
  controlId: string;
  controlRef?: string;
  controlTitle?: string;
  /** Called after the POST succeeds (before modal closes). */
  onSuccess?: () => void;
}

export function EvidenceCaptureModal({
  open,
  onClose,
  controlId,
  controlRef,
  controlTitle,
  onSuccess,
}: Props) {
  const uploadMutation = useUploadEvidence(controlId);
  const [draft, setDraft] = useState<EvidenceDraft>(emptyDraft);
  const [error, setError] = useState<string | null>(null);

  function update(patch: Partial<EvidenceDraft>) {
    setDraft((prev) => ({ ...prev, ...patch }));
    if (error) setError(null);
  }

  function handleClose() {
    setDraft(emptyDraft());
    setError(null);
    onClose();
  }

  async function handleSubmit() {
    if (!isDraftValid(draft)) return;
    uploadMutation.mutate(
      {
        title: draft.title.trim(),
        evidenceType: draft.evidenceType,
        contentSummary: buildContentSummary(draft.location, draft.notes),
      },
      {
        onSuccess: () => {
          toast.success('Evidence recorded');
          onSuccess?.();
          handleClose();
        },
        onError: (err: unknown) => {
          const msg =
            (err as { response?: { data?: { errors?: { message?: string }[] } } })
              ?.response?.data?.errors?.[0]?.message ?? 'Failed to save evidence';
          setError(msg);
        },
      },
    );
  }

  const headerTitle = [controlRef, controlTitle].filter(Boolean).join(' — ');

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">
            Add evidence
            {headerTitle && (
              <span className="text-text-secondary font-normal ml-1">· {headerTitle}</span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Advisory copy — D-F4-4, same text as SubmitWithEvidenceModal */}
        <div className="rounded-md border border-accent-sky/30 bg-accent-sky/5 px-3 py-2.5 text-xs text-text-primary">
          {EVIDENCE_ADVISORY_COPY}
        </div>

        {/* 5-field form */}
        <div className={cn('rounded-lg border border-border-subtle bg-bg-surface-2 p-3 space-y-2', error && 'border-accent-rose/40 bg-accent-rose/5')}>

          {/* Field 1: Title */}
          <div>
            <label className="text-[10px] text-text-secondary mb-1 block">
              Evidence title <span className="text-accent-rose">*</span>
            </label>
            <input
              type="text"
              value={draft.title}
              onChange={(e) => update({ title: e.target.value })}
              placeholder='e.g. "Access review Q1 2026" or "Firewall config export"'
              maxLength={255}
              className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-1.5 text-xs text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-1 focus:ring-accent-sky/50"
            />
          </div>

          {/* Field 2: Evidence type */}
          <div>
            <label className="text-[10px] text-text-secondary mb-1 block">
              Evidence type <span className="text-accent-rose">*</span>
            </label>
            <select
              value={draft.evidenceType}
              onChange={(e) => update({ evidenceType: e.target.value })}
              className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-1.5 text-xs text-text-primary focus:outline-none"
            >
              {EVIDENCE_TYPES.map((t) => (
                <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>

          {/* Field 3: Evidence date */}
          <div>
            <label className="text-[10px] text-text-secondary mb-1 block">
              Evidence date <span className="text-accent-rose">*</span>
            </label>
            <input
              type="date"
              value={draft.collectedAt}
              onChange={(e) => update({ collectedAt: e.target.value })}
              max={new Date().toISOString().substring(0, 10)}
              className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-1.5 text-xs text-text-primary focus:outline-none"
            />
          </div>

          {/* Field 4: Location / reference */}
          <div>
            <label className="text-[10px] text-text-secondary mb-1 block">Location / reference</label>
            <textarea
              value={draft.location}
              onChange={(e) => update({ location: e.target.value })}
              placeholder="SharePoint link, file path, system name, ticket ID, document reference"
              rows={2}
              maxLength={500}
              className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-1.5 text-xs text-text-primary placeholder:text-text-secondary focus:outline-none resize-none"
            />
          </div>

          {/* Field 5: Notes */}
          <div>
            <label className="text-[10px] text-text-secondary mb-1 block">Notes</label>
            <textarea
              value={draft.notes}
              onChange={(e) => update({ notes: e.target.value })}
              placeholder="Any context auditors need — who produced it, scope or limitations, external party if applicable"
              rows={3}
              maxLength={2000}
              className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-1.5 text-xs text-text-primary placeholder:text-text-secondary focus:outline-none resize-none"
            />
          </div>

          {error && <p className="text-[10px] text-accent-rose">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 pt-1 border-t border-border-subtle">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs"
            onClick={handleClose}
            disabled={uploadMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            size="sm"
            className="text-xs"
            onClick={handleSubmit}
            disabled={!isDraftValid(draft) || uploadMutation.isPending}
          >
            {uploadMutation.isPending ? 'Saving…' : 'Add evidence'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
